import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import Cookies from 'js-cookie';

const CreateUserModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'customer',
    status: 'active'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Agent permissions
  const [availableAgents, setAvailableAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [permissions, setPermissions] = useState({
    agents: {},
    admin: {},
    features: {}
  });
  const [showPermissions, setShowPermissions] = useState(false);

  // Feature options
  const features = [
    { key: 'mcp_chat', label: 'MCP Chat' },
    { key: 'monitoring', label: 'System Monitoring' },
    { key: 'workflow_builder', label: 'Workflow Builder' }
  ];

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await fetch('/api/agents/list-for-permissions', {
        headers: {
          'Authorization': `Bearer ${Cookies.get('sankalvax_token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAvailableAgents(data.agents);
        // Initialize all agents with full permissions by default
        const defaultAgentPerms = {};
        data.agents.forEach(agent => {
          defaultAgentPerms[agent.id] = { read: true, write: true, execute: true };
        });
        setPermissions(prev => ({
          ...prev,
          agents: defaultAgentPerms,
          features: { mcp_chat: true, monitoring: false, workflow_builder: false }
        }));
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAgentPermissionChange = (agentId, permission, value) => {
    setPermissions(prev => ({
      ...prev,
      agents: {
        ...prev.agents,
        [agentId]: {
          ...prev.agents[agentId],
          [permission]: value
        }
      }
    }));
  };

  const handleFeatureChange = (key, value) => {
    setPermissions(prev => ({
      ...prev,
      features: {
        ...prev.features,
        [key]: value
      }
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    const { confirmPassword, ...userData } = formData;
    // Include permissions in user data
    userData.permissions = permissions;
    onSubmit(userData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Create New User</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <input
                type="email"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  minLength="8"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
                {formData.password && (
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password *
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  required
                  minLength="8"
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                />
                {formData.confirmPassword && (
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role *
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              >
                <option value="admin">Admin</option>
                <option value="customer">Customer</option>
                <option value="viewer">Viewer</option>
              </select>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Agent Permissions Section */}
          <div className="mb-4 border rounded-lg">
            <button
              type="button"
              onClick={() => setShowPermissions(!showPermissions)}
              className="w-full px-4 py-3 flex justify-between items-center bg-gray-50 hover:bg-gray-100 rounded-t-lg"
            >
              <span className="font-medium text-gray-700">Agent Permissions</span>
              {showPermissions ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {showPermissions && (
              <div className="p-4 border-t">
                {loadingAgents ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
                    <span className="text-gray-600">Loading agents...</span>
                  </div>
                ) : (
                  <>
                    {/* Agent Permissions */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Agents Access</h4>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {availableAgents.map(agent => (
                          <div key={agent.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-sm font-medium truncate flex-1">{agent.name}</span>
                            <div className="flex gap-3 ml-2">
                              <label className="flex items-center text-xs">
                                <input
                                  type="checkbox"
                                  className="mr-1"
                                  checked={permissions.agents[agent.id]?.read || false}
                                  onChange={(e) => handleAgentPermissionChange(agent.id, 'read', e.target.checked)}
                                />
                                Read
                              </label>
                              <label className="flex items-center text-xs">
                                <input
                                  type="checkbox"
                                  className="mr-1"
                                  checked={permissions.agents[agent.id]?.write || false}
                                  onChange={(e) => handleAgentPermissionChange(agent.id, 'write', e.target.checked)}
                                />
                                Write
                              </label>
                              <label className="flex items-center text-xs">
                                <input
                                  type="checkbox"
                                  className="mr-1"
                                  checked={permissions.agents[agent.id]?.execute || false}
                                  onChange={(e) => handleAgentPermissionChange(agent.id, 'execute', e.target.checked)}
                                />
                                Execute
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Feature Access */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Feature Access</h4>
                      <div className="grid grid-cols-3 gap-2">
                        {features.map(feature => (
                          <label key={feature.key} className="flex items-center text-sm">
                            <input
                              type="checkbox"
                              className="mr-2"
                              checked={permissions.features[feature.key] || false}
                              onChange={(e) => handleFeatureChange(feature.key, e.target.checked)}
                            />
                            {feature.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Create User
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateUserModal;
